import { useCallback, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { orDash } from "../format";
import { useCompact, useDismiss } from "../hooks";
import { openedFrom, SECTION_ORDER, SECTIONS, type SectionKey } from "../sections";
import { useSession } from "../session/SessionContext";
import { ChevronRightIcon } from "./icons";
import { Modal } from "./Modal";
import { ToolbarSlotProvider } from "./PageHead";
import { RoleChip } from "./StatusChip";

/**
 * The shell: a fixed left rail, and everything else in the column beside it.
 *
 * A sidebar rather than a top bar because this is a tool people keep open. The
 * rail holds the whole surface at once (nothing hides behind a menu), the
 * identity it is all scoped to sits at the bottom where it is out of the way
 * but never in question, and the content column keeps its full height for the
 * tables that are the actual work. Sign out is one step behind that identity,
 * in a popover, so the rail never offers a one-click way out beside the
 * things people click all day.
 *
 * On a phone the same destinations become a tab bar on the bottom edge: always
 * in view, always under the thumb, and the height a wrapped rail spent at the
 * top goes back to the page. The identity moves behind the last tab, as the
 * account sheet, because a phone has no out-of-the-way corner to park it in.
 *
 * Above the content column sits the toolbar, a material the page fills with
 * its trail (components/PageHead.tsx). It belongs to the shell rather than to
 * the page so it spans the whole column, edge to edge, however wide the
 * window is.
 */
export function AppLayout() {
  const { staff } = useSession();
  const compact = useCompact();
  const isAdmin = staff?.role === "admin";
  const [toolbar, setToolbar] = useState<HTMLElement | null>(null);
  const sections = SECTION_ORDER.filter((key) => isAdmin || !SECTIONS[key].adminOnly);

  return (
    <div className="shell">
      {compact ? <TabBar sections={sections} /> : <Sidebar sections={sections} />}

      <main className="main">
        <div className="toolbar" ref={setToolbar} />
        <ToolbarSlotProvider value={toolbar}>
          <Outlet />
        </ToolbarSlotProvider>
      </main>
    </div>
  );
}

/**
 * Which destination is "here". A member page is not one of them, but it is
 * reached from one, and the rail keeps that one lit so it still answers
 * where you are.
 */
function useCurrentSection(): SectionKey | null {
  const location = useLocation();
  return location.pathname.startsWith("/users/") ? openedFrom(location.state) : null;
}

/** NavLink's own match, or the section a member page was opened from. */
function navClass(base: string | undefined, key: SectionKey, current: SectionKey | null) {
  return ({ isActive }: { isActive: boolean }) =>
    [base, isActive || key === current ? "active" : null].filter(Boolean).join(" ");
}

function Sidebar({ sections }: { sections: SectionKey[] }) {
  const { staff } = useSession();
  const current = useCurrentSection();
  const email = staff?.email ?? null;

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <img className="sidebar-mark" src={`${import.meta.env.BASE_URL}nerona-icon.svg`} alt="" />
        <span className="sidebar-title">
          Nerona
          <span className="sidebar-kicker">Console</span>
        </span>
      </div>

      <nav className="sidebar-nav">
        {sections.map((key) => {
          const { to, label, Icon } = SECTIONS[key];
          return (
            <NavLink key={key} to={to} end={to === "/"} className={navClass(undefined, key, current)}>
              <Icon />
              {label}
            </NavLink>
          );
        })}
      </nav>

      <div className="sidebar-foot">
        <AccountMenu email={email} />
      </div>
    </aside>
  );
}

/**
 * The rail's identity row, as the trigger for who is signed in and the way
 * out. The popover opens upward from the row it belongs to, the way a Mac
 * sidebar's account menu does, rather than as a modal in the middle of the
 * page: it is a glance and one action, not a task.
 */
function AccountMenu({ email }: { email: string | null }) {
  const { logout } = useSession();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);

  return (
    <div className="menu-wrap" ref={ref}>
      <button
        type="button"
        className="whoami-button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <Whoami email={email} />
        <ChevronRightIcon />
      </button>
      {open ? (
        <div className="account-pop" role="dialog" aria-label="Account">
          <AccountCard />
          <button type="button" className="danger solid wide" onClick={logout}>
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

function TabBar({ sections }: { sections: SectionKey[] }) {
  const { staff } = useSession();
  const current = useCurrentSection();
  const [account, setAccount] = useState(false);
  const email = staff?.email ?? null;

  return (
    <>
      <nav className="tabbar" aria-label="Sections">
        {sections.map((key) => {
          const { to, label, Icon } = SECTIONS[key];
          return (
            <NavLink key={key} to={to} end={to === "/"} className={navClass("tab", key, current)}>
              <Icon />
              {label}
            </NavLink>
          );
        })}
        <button
          type="button"
          className="tab"
          aria-haspopup="dialog"
          aria-expanded={account}
          onClick={() => setAccount(true)}
        >
          <Initial email={email} />
          Account
        </button>
      </nav>

      {account ? <AccountSheet onClose={() => setAccount(false)} /> : null}
    </>
  );
}

/**
 * Who is signed in, in full, and the way out. The rail truncates the email
 * and leaves the rest to a hover tooltip, which a finger cannot raise.
 */
function AccountSheet({ onClose }: { onClose: () => void }) {
  const { logout } = useSession();

  return (
    <Modal labelledBy="account-title" onClose={onClose}>
      {(close) => (
        <>
          <h2 id="account-title">Signed in</h2>
          <AccountCard />
          <div className="row dialog-actions">
            <button type="button" onClick={close}>
              Close
            </button>
            <button type="button" className="danger solid" onClick={logout}>
              Sign out
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

/** The identity in full, for the account sheet and popover. */
function AccountCard() {
  const { staff } = useSession();
  const email = staff?.email ?? null;
  return (
    <div className="account">
      <Initial email={email} large />
      <div className="stack account-text">
        <span className="account-email">{orDash(email)}</span>
        {staff ? <RoleChip role={staff.role} /> : null}
      </div>
    </div>
  );
}

function Whoami({ email }: { email: string | null }) {
  const { staff } = useSession();
  return (
    <div className="whoami">
      <Initial email={email} />
      <span className="whoami-text">
        <span className="whoami-email" title={email ?? undefined}>
          {orDash(email)}
        </span>
        {staff ? <RoleChip role={staff.role} /> : null}
      </span>
    </div>
  );
}

/** Initial, not a photo: there is no avatar to fetch, and a letter still tells
    two accounts apart at a glance. */
function Initial({ email, large = false }: { email: string | null; large?: boolean }) {
  return (
    <span className={large ? "avatar avatar-lg" : "avatar"} aria-hidden="true">
      {(email ?? "?").charAt(0).toUpperCase()}
    </span>
  );
}
